import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkle, FilePdf, CaretDown, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtCarbs = (n) => `${Math.round((n || 0) * 10) / 10}g`;

export default function History() {
  const [data, setData] = useState(null);
  const [openDate, setOpenDate] = useState(null);
  const [dayDetail, setDayDetail] = useState({}); // { [date]: { meals, total_carbs_g } }
  const [loadingDate, setLoadingDate] = useState(null);

  const load = () => {
    api.get("/meals/history").then((r) => setData(r.data)).catch(() => setData({ days: [] }));
  };
  useEffect(() => { load(); }, []);

  const toggleDate = async (date) => {
    if (openDate === date) { setOpenDate(null); return; }
    setOpenDate(date);
    if (!dayDetail[date]) {
      setLoadingDate(date);
      try {
        const { data } = await api.get(`/meals?date=${date}`);
        setDayDetail((d) => ({ ...d, [date]: data }));
      } catch (e) {
        toast.error("Couldn't load that day");
      } finally {
        setLoadingDate(null);
      }
    }
  };

  const deleteMeal = async (mealId, date) => {
    try {
      await api.delete(`/meals/${mealId}`);
      // refresh that day's detail and overall list
      const { data: dayData } = await api.get(`/meals?date=${date}`);
      setDayDetail((d) => ({ ...d, [date]: dayData }));
      load();
      toast.success("Scan deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get("/reports/weekly.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "tally-weekly-report.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate report");
    }
  };

  if (!data) {
    return <div className="p-12 text-center text-[#5C6B62] font-serif-display text-2xl">Loading…</div>;
  }

  const fmtDate = (s) =>
    new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div className="px-5 pt-4 pb-2">
      <span className="label-eyebrow">{data.limited ? `Past ${data.limit_days} days` : "Past 30 days"}</span>
      <h1 className="font-serif-display text-3xl tracking-tight text-[#1A2421] mt-1">Your scans</h1>
      <p className="text-sm text-[#5C6B62] mt-2">
        Tap any day to see the scans inside. Your scans stay here forever — tap the trash to delete one.
      </p>

      <button
        onClick={downloadPdf}
        className="btn-primary w-full mt-5 flex items-center justify-center gap-2"
        data-testid="download-pdf-button-history"
      >
        <FilePdf size={16} weight="regular" /> Download weekly doctor report
      </button>

      {data.limited && (
        <Link
          to="/profile?focus=upgrade"
          className="mt-4 flex items-center justify-between gap-3 card-organic p-4 hover:bg-[#F0EFE9] transition-colors"
          data-testid="history-upgrade-banner"
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#C76E4B] flex items-center justify-center">
              <Sparkle size={14} weight="fill" className="text-white" />
            </span>
            <div className="text-xs">
              <div className="text-[#1A2421] font-medium">Free shows only {data.limit_days} days.</div>
              <div className="text-[#5C6B62]">Upgrade to see full 30-day history + CSV export.</div>
            </div>
          </div>
        </Link>
      )}

      {data.days.length === 0 ? (
        <div className="card-organic p-8 mt-8 text-center" data-testid="history-empty">
          <p className="font-serif-display text-xl text-[#1A2421]">No scans yet.</p>
          <p className="text-sm text-[#5C6B62] mt-2">Take a photo on the Snap tab to begin.</p>
        </div>
      ) : (
        <ul className="mt-6 space-y-2.5" data-testid="history-list">
          {data.days.map((d) => {
            const isOpen = openDate === d.date;
            const detail = dayDetail[d.date];
            return (
              <li key={d.date} className="card-organic overflow-hidden" data-testid={`history-row-${d.date}`}>
                <button
                  onClick={() => toggleDate(d.date)}
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-[#F0EFE9] transition-colors text-left"
                  data-testid={`history-row-toggle-${d.date}`}
                  aria-expanded={isOpen}
                >
                  <div className="min-w-0 flex items-center gap-3">
                    <CaretDown
                      size={14}
                      weight="bold"
                      className={`text-[#5C6B62] transition-transform shrink-0 ${isOpen ? "rotate-0" : "-rotate-90"}`}
                    />
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-[#5C6B62]">{fmtDate(d.date)}</div>
                      <div className="text-sm text-[#1A2421] mt-0.5">
                        {d.meal_count} scan{d.meal_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif-display text-2xl text-[#2B422F]">{fmtCarbs(d.total_carbs_g)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C6B62] mt-0.5">carbs</div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#E6E3D8] p-4 space-y-3 bg-[#F9F8F6]" data-testid={`history-detail-${d.date}`}>
                    {loadingDate === d.date && !detail ? (
                      <p className="text-sm text-[#5C6B62] italic">Loading…</p>
                    ) : !detail || detail.meals.length === 0 ? (
                      <p className="text-sm text-[#5C6B62] italic">No scans on this day yet.</p>
                    ) : (
                      detail.meals.map((m) => (
                        <div key={m.meal_id} className="rounded-2xl bg-white border border-[#E6E3D8] p-3" data-testid={`history-meal-${m.meal_id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-[11px] uppercase tracking-[0.14em] text-[#5C6B62]">{fmtTime(m.created_at)}</div>
                              <div className="font-serif-display text-lg text-[#2B422F] mt-0.5">{fmtCarbs(m.total_carbs_g)}</div>
                            </div>
                            <button
                              onClick={() => deleteMeal(m.meal_id, d.date)}
                              className="text-[#5C6B62] hover:text-[#C76E4B] p-1"
                              data-testid={`history-delete-${m.meal_id}`}
                              aria-label="Delete scan"
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                          <ul className="mt-2 space-y-0.5">
                            {m.ingredients.map((ing, i) => (
                              <li key={i} className="text-xs text-[#1A2421] flex items-center justify-between gap-2">
                                <span className="truncate">
                                  {ing.name}
                                  {ing.portion ? <span className="text-[#5C6B62]"> · {ing.portion}</span> : null}
                                </span>
                                <span className="text-[#5C6B62] tabular-nums shrink-0">{fmtCarbs(ing.carbs_g)}</span>
                              </li>
                            ))}
                          </ul>
                          {m.notes && (
                            <p className="text-[11px] text-[#5C6B62] italic mt-2 border-t border-[#E6E3D8] pt-2">
                              {m.notes}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
